import React from 'react';

// WeatherData type based on weather-agent-enhanced output
export interface WeatherData {
  location: string;
  temperature: number;
  description: string;
  humidity: number;
  windSpeed: number;
  pressure: number;
  feelsLike: number;
  visibility: number | null;
  icon: string; // OpenWeatherMap icon code
  backgroundImage: string;
  isDay: boolean;
  condition: string;
  uvIndex?: number;
  airQuality?: string;
  tempMax?: number;
  tempMin?: number;
  rain?: number;
}

// Map OpenWeatherMap icon code to emoji or SVG (can be extended)
const iconMap: Record<string, string> = {
  '01d': '☀️',
  '01n': '🌙',
  '02d': '🌤️',
  '02n': '🌤️',
  '03d': '☁️',
  '03n': '☁️',
  '04d': '☁️',
  '04n': '☁️',
  '09d': '🌧️',
  '09n': '🌧️',
  '10d': '🌦️',
  '10n': '🌦️',
  '11d': '⛈️',
  '11n': '⛈️',
  '13d': '❄️',
  '13n': '❄️',
  '50d': '🌫️',
  '50n': '🌫️',
};

// Match Discover weather art mapping
const getWeatherArtForCondition = (condition: string) => {
  const c = (condition || '').toLowerCase();
  if (c.includes('thunder') || c.includes('storm')) return '/weather/thunderstorm.png';
  if (c.includes('rain') || c.includes('drizzle') || c.includes('shower')) return '/weather/rain.png';
  if ((c.includes('cloud') && c.includes('sun')) || c.includes('partly')) return '/weather/partly-cloudy.png';
  if (c.includes('cloud') || c.includes('overcast')) return '/weather/cloudy.png';
  if (c.includes('sun') || c.includes('clear')) return '/weather/sunny.png';
  return '/weather/partly-cloudy.png';
};

const WeatherCard: React.FC<{ data: WeatherData; isDarkMode?: boolean }> = ({ data, isDarkMode = false }) => {
  // Fallbacks for high/low/rain if not present
  const tempMax = data.tempMax !== undefined && data.tempMax !== null ? Math.round(data.tempMax) : null;
  const tempMin = data.tempMin !== undefined && data.tempMin !== null ? Math.round(data.tempMin) : null;
  const rain = data.rain !== undefined && data.rain !== null ? Math.round(data.rain) : null;

  // Use emoji or OpenWeatherMap icon
  const icon = iconMap[data.icon] || '🌤️';
  const weatherArt = getWeatherArtForCondition(data.condition);
  const displayLocation = (data.location || '').split(',')[0];

  return (
    <div
      style={{
        background: `rgba(30, 41, 59, 0.65)`,
        borderRadius: 24,
        overflow: 'hidden',
        color: '#f8fafc',
        maxWidth: 340,
        margin: '15px auto',
        position: 'relative',
        backgroundImage: 'url(/src/assets/discoverPage/CardBG2.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <style>{`
        @keyframes floatWeatherArt { 0% { transform: translate3d(-4px, 0, 0) rotate(0deg) scale(1);} 50% { transform: translate3d(4px, -5px, 0) rotate(0.6deg) scale(1.015);} 100% { transform: translate3d(-4px, 0, 0) rotate(0deg) scale(1);} }
      `}</style>
      {/* Background image overlay */}
      <div
        style={{
          background: `url('${data.backgroundImage}') center/cover no-repeat`,
          position: 'absolute',
          inset: 0,
          opacity: 0.4,
          zIndex: 0,
        }}
      />
      {/* Dark overlay for readability */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.49)',
          zIndex: 1,
        }}
      />
      <div style={{ position: 'relative', zIndex: 2, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 16, opacity: 0.9 }}>{displayLocation}</div>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 8px',
            borderRadius: 999,
            background: data.isDay ? 'rgba(250, 204, 21, 0.18)' : 'rgba(59, 130, 246, 0.18)',
            color: data.isDay ? '#fde68a' : '#93c5fd',
          }}>
            {data.isDay ? 'Day' : 'Night'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <img
            src={weatherArt}
            alt={data.condition}
            style={{
              width: 130,
              height: 130,
              marginRight: 18,
              objectFit: 'contain',
              display: 'block',
              position: 'relative',
              zIndex: 2,
              mixBlendMode: isDarkMode ? 'screen' as const : 'multiply' as const,
              animation: 'floatWeatherArt 8s ease-in-out infinite',
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              <div style={{ fontSize: 48, fontWeight: 700, lineHeight: 1 }}>{data.temperature}°C</div>
              {typeof data.uvIndex === 'number' && data.uvIndex > 0 && (
                <div style={{ fontSize: 12, opacity: 0.9, padding: '4px 8px', borderRadius: 8, background: 'rgba(34,197,94,0.15)', color: '#86efac' }}>
                  UV {Math.round(data.uvIndex)}
                </div>
              )}
            </div>
            <div style={{ fontSize: 16, opacity: 0.85, textTransform: 'capitalize' }}>{data.description}</div>
          </div>
        </div>
        {!(tempMax === null && tempMin === null) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              <span style={{ fontWeight: 600 }}>High:</span> {tempMax !== null ? `${tempMax}°` : '--'}
            </div>
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              <span style={{ fontWeight: 600 }}>Low:</span> {tempMin !== null ? `${tempMin}°` : '--'}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ fontSize: 18 }}>💨</span>
              <span style={{ fontSize: 13, opacity: 0.8 }}>Wind</span>
            </div>
            <div style={{ fontWeight: 600 }}>{data.windSpeed} km/h</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ fontSize: 18 }}>💧</span>
              <span style={{ fontSize: 13, opacity: 0.8 }}>Humidity</span>
            </div>
            <div style={{ fontWeight: 600 }}>{data.humidity}%</div>
          </div>
        </div>
        {/* Extra row: Pressure, Visibility */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 10 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>🧭</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>Pressure</div>
            <div style={{ fontWeight: 600 }}>{data.pressure} hPa</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>👁️</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>Visibility</div>
            <div style={{ fontWeight: 600 }}>{data.visibility !== null ? `${data.visibility} km` : '--'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherCard;
